/** 결제가 거절됐을 때 셀러에게 할 말. `edit-messages.ts` 와 같은 규율이다.
 *
 * ⛔ **서버 메시지(`error`)를 그대로 띄우지 않는다.** 그쪽은 영어이고 구현 용어이며, 바뀔 때
 * 화면 문구가 함께 바뀐다. **코드는 계약이고 문장은 우리 것이다**(`lib/api.ts` 머리말).
 *
 * ⛔ **PG 가 준 사유 문장도 안 띄운다.** 서버가 애초에 안 내려 준다(`routes/payments.ts`).
 *
 * ## ⛔ 「실패했습니다」를 기본 문구로 쓰지 않는다
 *
 * 결제 경로에는 **돈이 움직였는지 우리도 모르는 갈래가 하나 있다**(`PAYMENT_GATEWAY_FAILED`.
 * 502). 거기에 「실패했습니다」라고 단정하면 셀러는 **돈을 낸 채로 다시 결제한다.** 그래서
 * 이 표는 셋을 다른 문장으로 가른다.
 *   - 돈이 안 움직였다 (다시 하면 된다)
 *   - 이미 결제됐다 (다시 하면 안 된다)
 *   - 모른다 (⛔ 다시 하라고도, 실패했다고도 말하지 않는다)
 *
 * ## ⚠️ 여기 있는 문장 넷은 **정본이 아직 없다** (2026-09-25)
 *
 * 목업 `A-26`·`W-26` 에 결제 실패 문구가 한 벌 있고(`카드 승인이 안 됐어요` …) **그것은
 * 그대로 썼다.** 그런데 결제사를 붙이면서 목업에 없던 상태가 넷 생겼다.
 *   ① 결제 준비가 아직 안 된 배포(서버 키가 비어 있다)
 *   ② 결제창을 못 불러왔다
 *   ③ 결제가 끝났는지 모른다
 *   ④ 결제한 장수보다 담긴 사진이 많다
 * 아래 문장은 **규칙만 지킨 임시**다(재촉하지 않는다 · 비활성 사유를 항상 보이게 · 돈 낱말은
 * 용어 표를 따른다). **정본은 `design-manager` 가 목업에 세운다.**
 */

import { REFUND_ARRIVAL_NOTICE, formatKrw } from "@makit/shared";

import { ApiError } from "./api";

export interface PaymentMessage {
  /** 셀러에게 보이는 한 문장. 마침표로 끝난다. */
  text: string;
  /** 같은 동작을 다시 걸면 될 수 있는가. `false` 면 화면이 재시도를 권하지 않는다. */
  retryable: boolean;
}

/* 포인트를 섞은 결제의 문장 셋(아래 「포인트를 섞은 결제」 절). 표보다 먼저 선언한다. */
const POINTS_CHANGED_LINE = "남은 포인트가 바뀌어 금액을 다시 맞췄습니다.";
const POINTS_OFF_LINE = "지금은 포인트를 쓸 수 없어 금액을 다시 맞췄습니다.";
const CARD_REVERTED_REASON_LINE = "남은 포인트가 바뀌어 이 금액으로는 결제할 수 없었습니다. 금액을 확인하고 다시 눌러 주세요.";
const CONFIRMING_TEXT = "결제를 확인하는 중입니다. 잠시 뒤 다시 눌러 주세요.";

const BY_CODE: Record<string, PaymentMessage> = {
  // ── 돈이 움직이지 않은 것이 확실한 것들 ────────────────────────────────────
  //
  // 목업 `W-26` 의 `결제 실패했을 때` 가 쓰는 문장이다. **여기서 새로 적지 않는다.**
  PAYMENT_DECLINED: {
    text: "카드 승인이 안 됐어요. 한도가 넘었거나 카드사에서 막았을 수 있습니다.",
    retryable: true,
  },
  // 금액이 갈렸다. 같은 값으로 다시 보내면 몇 번이든 같은 답이다. 화면이 할 일은
  // **금액을 다시 받아 오는 것**이고, 그래서 재시도를 권하지 않는다.
  PAYMENT_AMOUNT_MISMATCH: {
    text: "결제할 금액이 바뀌었습니다. 이 화면을 새로 열면 바뀐 금액으로 결제하실 수 있습니다.",
    retryable: false,
  },
  PAYMENT_ORDER_MISMATCH: {
    text: "이 작업의 결제 정보가 아닙니다. 이 화면을 새로 열어 주세요.",
    retryable: false,
  },
  NOTHING_TO_PAY: {
    text: "결제할 사진이 없습니다. 사진을 먼저 담아 주세요.",
    retryable: false,
  },
  TOO_MANY_IMAGES: {
    text: "담긴 사진이 한 작업에 담을 수 있는 수를 넘었습니다.",
    retryable: false,
  },

  // ── 이미 끝난 것 ───────────────────────────────────────────────────────────
  //
  // ⛔ 실패가 아니다. 다시 결제하라고 말하면 **한 작업에 두 번 결제하려 들게 된다.**
  PAYMENT_ALREADY_PAID: {
    text: "이 작업은 이미 결제되셨습니다. 바로 시작하시면 됩니다.",
    retryable: false,
  },

  // ── ⛔ 모르는 것 ───────────────────────────────────────────────────────────
  //
  // ⛔⛔ **「실패했습니다」라고 쓰지 마라.** 승인이 섰을 수도 있다. 셀러가 이 문장을 읽고
  //      다시 결제하면 같은 작업에 두 번 낸다. **다시 하라고도 말하지 않는다.**
  PAYMENT_GATEWAY_FAILED: {
    text: "결제가 끝났는지 아직 확인되지 않았습니다. 잠시 뒤 이 화면을 다시 열어 확인해 주세요.",
    retryable: false,
  },

  // ── 우리 쪽 준비가 안 된 것 ────────────────────────────────────────────────
  //
  // ⛔ **셀러 잘못이 아니다.** 다시 눌러도 같은 답이 오므로 재시도를 권하지 않는다.
  PAYMENT_NOT_CONFIGURED: {
    text: "아직 결제를 받을 수 없습니다. 준비가 끝나면 이 자리에서 바로 하실 수 있습니다.",
    retryable: false,
  },

  // ── 결제한 장수보다 담긴 사진이 많은 것 ────────────────────────────────────
  //
  // ⛔ **「결제해 주세요」로 뭉개지 마라.** 셀러는 자기가 결제했다는 것을 안다.
  // ⚠️ **오늘은 셀러가 여기서 할 수 있는 일이 없다.** 담긴 사진을 한 장씩 빼는 자리도
  //    (PRD `7.13` `D-54`) 한 작업에 두 번째로 결제하는 자리도 아직 없다. **그 사실을
  //    숨기지 않는다** — 숨기면 셀러는 없는 버튼을 찾는다.
  PAYMENT_COVERAGE_EXCEEDED: {
    text: "결제하신 뒤에 담은 사진이 있습니다. 결제하신 장수만큼만 만들어 드릴 수 있는데, 여기서 사진을 빼거나 더 결제하는 자리가 아직 없습니다.",
    retryable: false,
  },
  PAYMENT_REQUIRED: {
    text: "이 작업은 아직 결제 전입니다. 결제하시면 그 자리에서 시작합니다.",
    retryable: false,
  },
  PAYMENT_IN_PROGRESS: {
    text: CONFIRMING_TEXT,
    retryable: true,
  },
  PAYMENT_FAILED: {
    text: "앞서 결제가 되지 않았습니다. 다시 결제하시면 그 자리에서 시작합니다.",
    retryable: true,
  },
  PAYMENT_REFUNDED: {
    text: "이 작업은 금액이 전부 돌아갔습니다. 새 작업으로 다시 해 주세요.",
    retryable: false,
  },

  // ── 포인트를 섞은 결제 (`W-26`. 2026-10-27) ─────────────────────────────────
  //
  // 토스를 부르기 전에 포인트 쪽에서 거절됐다. 화면은 금액과 잔액을 다시 받는다(`Pay.tsx` `pointsConfirmFallback`).
  // 다시 받은 금액으로 다시 누르면 되므로 `retryable` 이 참이다.
  POINTS_INSUFFICIENT: { text: POINTS_CHANGED_LINE, retryable: true },
  POINTS_DEBT: { text: POINTS_CHANGED_LINE, retryable: true },
  // ⚠️ 계정이 정지된 갈래의 문장은 정본에 따로 없다. 셀러 쪽 결과(그 포인트를 쓸 수 없게 됐다)가 같아 같은 말을 쓴다.
  POINTS_ACCOUNT_NOT_ACTIVE: { text: POINTS_CHANGED_LINE, retryable: true },
  POINTS_SPEND_OFF: { text: POINTS_OFF_LINE, retryable: true },
  // ⭐ **같은 키로 다시 보내면 서버가 이어서 맞추는 갈래**다(원장 순번을 못 쥐었다 · 카드 승인 뒤 원장을 못 썼다). 「확인 중」과
  //    같은 처지라 같은 문장을 쓰고 `다시 시도` 를 연다. ⛔ 「실패했습니다」로 단정하지 않는다. 카드가 승인됐을 수 있다.
  POINTS_BUSY: { text: CONFIRMING_TEXT, retryable: true },
  POINTS_SPEND_FAILED: { text: CONFIRMING_TEXT, retryable: true },
};

/** 서버가 안 준 갈래. **연결이 끊긴 것과 우리가 모르는 코드를 가른다.** */
const OFFLINE: PaymentMessage = {
  text: "연결이 끊겼습니다. 잠시 뒤 다시 눌러 주세요.",
  retryable: true,
};

/** ⛔ **기본 문구가 「실패했습니다」가 아니다.** 여기 오는 것은 우리가 모르는 코드이고,
 *  모르는 것을 실패로 단정하면 이 파일 머리말이 막으려던 자리를 그대로 밟는다. */
const UNKNOWN: PaymentMessage = {
  text: "결제를 끝내지 못했습니다. 잠시 뒤 이 화면을 다시 열어 확인해 주세요.",
  retryable: false,
};

export function paymentMessage(err: unknown): PaymentMessage {
  if (!(err instanceof ApiError)) return UNKNOWN;
  // `ApiError(0)` 은 응답 자체를 못 받은 것이다(`lib/api.ts`).
  if (err.status === 0) return OFFLINE;
  // 포인트를 섞은 승인이 **카드 승인 뒤** 포인트를 못 빼 카드를 되돌렸거나 되돌리는 중이다(`cardReverted` 가 실림).
  const reverted = cardRevertedLines(err);
  if (reverted) return { text: reverted.join(" "), retryable: true };
  const hit = err.code ? BY_CODE[err.code] : undefined;
  return hit ?? UNKNOWN;
}

/* ── 포인트를 섞은 결제 (`W-26`. 2026-10-27) ──────────────────────────────────────────────
 *
 * 글자의 정본은 목업 `W-26` 오른쪽 「결제창에서 돌아왔는데 금액이 바뀌었을 때」 · 「카드 승인 뒤 되돌렸을 때」와
 * PRD `7.7.3a` 「`W-26` 포인트 쓰기 칸 · 2026-10-27 확정분」 · `7.5.9` 「2026-10-27 결정에서 온 낱말」이다. 여기서 새로 짓지 않는다.
 * ⛔ `잔액 부족` · `오류가 발생했습니다` · `잠시 뒤 다시 시도해 주세요` 는 쓰지 않는 말이다(`7.5.9`). */


/** 남은 포인트를 쓰지 못하게 된 거절 코드. 이 셋만 「남은 포인트가 바뀌어」라는 사유를 붙인다. */
const BALANCE_REFUSAL_CODES: readonly string[] = ["POINTS_INSUFFICIENT", "POINTS_DEBT", "POINTS_ACCOUNT_NOT_ACTIVE"];

/** 카드 승인 뒤 되돌림의 문장(목업 `W-26` 「카드 승인 뒤 되돌렸을 때」). 해당 없으면 `null`.
 *
 * - `cardReverted: true` (토스 취소가 성공한 기록이 있다): `승인된 카드 결제 ₩N을 취소했습니다.`
 * - `cardReverted: false` (확인 전): `승인된 카드 결제 ₩N이 돌아가는 중입니다.` ⛔ 확인 전에 `취소했습니다`를 쓰지 않는다(법무 `L-F66`-7)
 * - 둘째 줄은 남은 포인트 때문에 막힌 경우에만, 셋째 줄은 `REFUND_ARRIVAL_NOTICE` 그대로다.
 *
 * ⚠️ `POINTS_SPEND_FAILED` + `false` 는 여기 오지 않는다. 그 갈래는 크론이 포인트를 마저 뺄 수도, 카드를 되돌릴 수도 있어서
 *    「돌아가는 중」이 거짓일 수 있다. 그래서 「확인 중」 문장(위 표)으로 둔다. */
export function cardRevertedLines(err: unknown): string[] | null {
  if (!(err instanceof ApiError)) return null;
  const reverted = (err as { cardReverted?: unknown }).cardReverted;
  const amount = (err as { tossAmount?: unknown }).tossAmount;
  if (typeof reverted !== "boolean" || typeof amount !== "number" || !(amount > 0)) return null;
  const balanceRefusal = err.code !== undefined && BALANCE_REFUSAL_CODES.includes(err.code);
  if (!reverted && !balanceRefusal) return null;
  const first = reverted
    ? `승인된 카드 결제 ${formatKrw(amount)}을 취소했습니다.`
    : `승인된 카드 결제 ${formatKrw(amount)}이 돌아가는 중입니다.`;
  return [first, ...(balanceRefusal ? [CARD_REVERTED_REASON_LINE] : []), REFUND_ARRIVAL_NOTICE];
}

/** 결제창에서 돌아왔는데(또는 포인트로만을 눌렀는데) 서버가 포인트 쪽에서 거절해 금액을 다시 맞췄을 때의 `!` 배너 두 줄.
 *
 * @param kind `CHANGED` 남은 포인트가 바뀌었다 · `OFF` 우리가 포인트 쓰기를 꺼 두었다
 * @param split 다시 맞춘 두 몫. 고른 갈래가 더는 서지 않아 고름을 비웠으면 `null` 이고 그때는 둘째 줄이 없다
 *   (금액이 정해지지 않았다. 저절로 다른 갈래를 고르지 않는다) */
export function pointsAdjustedLines(
  kind: "CHANGED" | "OFF",
  split: { pointAmount: number; tossAmount: number } | null,
): string[] {
  const first = kind === "OFF" ? POINTS_OFF_LINE : POINTS_CHANGED_LINE;
  if (split === null) return [first];
  const amounts =
    split.pointAmount > 0
      ? `포인트에서 ${formatKrw(split.pointAmount)} · 결제 수단으로 ${formatKrw(split.tossAmount)}입니다.`
      : `결제 수단으로 ${formatKrw(split.tossAmount)}입니다.`;
  return [first, `${amounts} 확인하고 다시 눌러 주세요.`];
}

/** 결제창이 실패로 끝나 되돌려 보낸 코드. **토스의 코드를 우리 표에 섞지 않는다.**
 *
 * ⛔ 그쪽 코드 목록을 여기 베끼면 **그 목록이 낡는 날 화면이 틀린 말을 한다.** 서버는 이미
 * 그 분류를 하고 있고(`lib/pg.ts` 의 `classify`) 그 판정은 승인 단계의 것이다. 결제창이
 * 스스로 끊은 경우에 셀러가 알아야 하는 것은 **돈이 안 나갔다는 사실 하나**다.
 *
 * ⚠️ **한 가지만 가른다**: 셀러가 스스로 닫은 것인가. 그것까지 「안 됐어요」라고 말하면
 * 화면이 방금 있었던 일을 모르는 것처럼 굴게 된다. */
export function widgetFailureMessage(code: string): PaymentMessage {
  if (isWidgetSelfCancel(code)) {
    return { text: "결제를 그만두셨습니다. 올린 사진은 그대로 있습니다.", retryable: true };
  }
  return BY_CODE.PAYMENT_DECLINED as PaymentMessage;
}

/** 셀러가 결제창을 **스스로 닫았는가**. 화면이 「안 됐어요」라고 말하지 않고, 서버에 실패로 보고하지도
 *  않는 갈래다(`docs/ERROR_LOGGING.md` §3-4-5 (바)). 위 `widgetFailureMessage` 와 한 판정을 쓴다.
 *  두 자리가 각자 목록을 들면 한쪽만 낡는다. */
export function isWidgetSelfCancel(code: string): boolean {
  return code === "USER_CANCEL" || code === "PAY_PROCESS_CANCELED";
}
